import * as Sentry from '@sentry/react';
import { checkingEmptyObject } from './checkingEmptyObj';
import axiosInstance from './axiosInstance';
import {
  BASE_URL,
  NOTIFY_URL,
  DAMAGES_BMW,
  AGENT_TOKEN,
  // BASE_URL_V1,
  NOTIFY_TOKEN,
  AGENT_BASE_URL,
  IMG_URL_UPLOAD,
  DAMAGES_VERSION,
  PAVE_PASS_API_KEY,
  BASE_DAMAGES_TIRES,
  PAVE_PASS_BASE_URL,
  IMG_URL_UPLOAD_USER,
  OPEN_API,
  PAVE_LOCALIZATION,
  PAVE_FORMS_API,
  REACT_APP_PAVE_VEHICLES_API,
  // langsType,
} from './config';

import { directToPave } from '~/utils/redirectURL';
import {store} from '../state/store';

// import { parseUrlToQuery } from './parseToQuery';
import { openNotification } from '~/utils/antNotification';

const validateRequestData = (method, data, url) => {
  const errors = [];
  
  // Common validations
  if (method === 'POST' || method === 'PUT') {
    if (!data) {
      errors.push('Request data is required for POST/PUT requests');
    }
    
    // Photo upload specific validations
    if (url.includes('/photos') && data instanceof FormData) {
      if (!data.get('image')) {
        errors.push('Image file is required for photo uploads');
      }
      if (!data.get('photo_code')) {
        errors.push('Photo code is required for photo uploads');
      }
      
      const imageFile = data.get('image');
      if (imageFile && imageFile.size === 0) {
        errors.push('Image file is empty');
      }
      if (imageFile && imageFile.size > 50 * 1024 * 1024) { // 50MB limit
        errors.push('Image file is too large (max 50MB)');
      }
    }
    
    // VIN decode validations
    if (url.includes('/vin') && data instanceof FormData) {
      const vin = data.get('vin');
      if (vin && (vin.length !== 17 || !/^[A-HJ-NPR-Z0-9]{17}$/i.test(vin))) {
        errors.push('Invalid VIN format (must be 17 characters, no I, O, or Q)');
      }
    }
    
    // Session validations
    if (url.includes('/session') && typeof data === 'object') {
      if (data.sessionID && !/^[a-zA-Z0-9-_]{10,}$/.test(data.sessionID)) {
        errors.push('Invalid session ID format');
      }
    }
  }
  
  return errors;
};

// create call api
export const callApis = (
  method,
  url,
  data,
  withToken = false,
  configs,
  customHeader = false,
  headerType = false
) => {
  const validationErrors = validateRequestData(method, data, url);
  if (validationErrors.length > 0) {
    if (window.Sentry) {
      Sentry.captureException(new Error(`Request validation failed: ${validationErrors.join(', ')}`), {
        tags: {
          error_type: 'validation_error',
          method,
          url: url.replace(/\/[a-f0-9-]{36}/g, '/[uuid]') // Hide UUIDs
        },
        extra: {
          validationErrors,
          hasData: !!data,
          dataType: data?.constructor?.name
        }
      });
    }
    
  }

  const headers = {};
  if (withToken) {
    headers.Authorization = `Bearer ${withToken}`;
  }
  if (customHeader) {
    headers['API-KEY'] = PAVE_PASS_API_KEY;
  }
  
  //  Enhanced error handling wrapper
  const executeRequest = async () => {
    try {
      switch (method) {
        case 'GET':
          if (checkingEmptyObject(headers) === 'EMPTY') {
            return axiosInstance({
              method,
              url,
            });
          }
          return axiosInstance({
            method,
            url,
            headers,
          });
        case 'POST':
          headers['Content-Type'] = 'multipart/form-data';
          if (configs) {
            return axiosInstance({
              method,
              url,
              data,
              headers,
              onUploadProgress: (progressEvent) => configs(progressEvent),
            });
          } else {
            if (headerType) {
              headers['Content-Type'] = 'application/json';
            }
            return axiosInstance({
              method,
              url,
              data,
              headers,
            });
          }
        case 'PUT':
          headers['Content-Type'] = 'application/json';
          return axiosInstance({
            method,
            url,
            data,
            headers,
          });

        case 'DELETE':
          headers['Content-Type'] = 'multipart/form-data';
          if (headerType) {
            headers['Content-Type'] = 'application/json';
          }
          return axiosInstance({
            method,
            url,
            data,
            headers,
          });
        default:
          headers['Content-Type'] = 'multipart/form-data';
          return axiosInstance({
            method,
            url,
            data,
            headers,
          });
      }
    } catch (error) {
      if (error.response?.status === 400) {
        console.error('Bad Request (400):', {
          url,
          method,
          data: data instanceof FormData ? '[FormData]' : data,
          response: error.response.data
        });
        
        // Add specific handling for common 400 errors
        const errorMessage = error.response.data?.message || error.message;
        if (errorMessage.includes('Invalid VIN')) {
          throw new Error('Invalid VIN format. Please check the VIN and try again.');
        }
        if (errorMessage.includes('Invalid session')) {
          throw new Error('Session has expired. Please refresh the page and try again.');
        }
        if (errorMessage.includes('Invalid photo')) {
          throw new Error('Photo upload failed. Please try taking the photo again.');
        }
        
        // Generic 400 error
        throw new Error(`Request failed: ${errorMessage}`);
      }
      
      throw error;
    }
  };
  
  return executeRequest();
};

// init functions

// 1. start session
// export const handleStartSession = (id) => {
//   const data = new FormData();
//   data.append('session_key', id);
//   return callApis('POST', `${BASE_URL}start`, data, false, false);
// };

export const handleStartSession = (sessionKey) => {
  return callApis('PUT', `${OPEN_API}v1/capture/${sessionKey}/start`);
};

// 2. user details
export const handleUserDetails = (userDetailsSubmit) => {
  const url = `${OPEN_API}v2/capture/sessions/${userDetailsSubmit.session_key}/devices`;
  const data = {
    device_id: userDetailsSubmit.device_id,
    ua: userDetailsSubmit.ua,
    device: userDetailsSubmit.device,
    browser: userDetailsSubmit.browser,
    os: userDetailsSubmit.os,
    resolution: userDetailsSubmit.resolution,
    phone_number: userDetailsSubmit.phone_number
  };
  return callApis('POST', url, data, false, false, false, true);
};

// 3. CheckProgress current Session
export const handleCheckingProgressMiddleware = (id) => {
  const url = `${OPEN_API}v1/capture/sessions/${id}/progress`;
  return callApis('GET', url, {}, false, false)
    .then((response) => {
      const { data } = response;
      const { status, status_reason } = data;

      // Handle invalid VIN exceeded case
      if (status === 'invalid' && status_reason === 'INVALID_VIN_EXCEEDED') {
        openNotification({
          time: 5,
          type: 'error',
          key: 'INVALID_VIN_EXCEEDED',
        });
        setTimeout(() => {
          directToPave(id);
        }, 1000);
      }

      // Handle invalid warning cases
      if (status === 'invalid_warning') {
        const { setSessionStatus } = require('../state/actions/sessionProcess/actions');
        store.dispatch(setSessionStatus({ 
          status: status,
          status_reason: status_reason
        }));
      }

      return response;
    })
    .catch((error) => {
      console.error('Error checking progress:', error);
      throw error;
    });
};

export const handleCheckingProgress = (id) => {
  return handleCheckingProgressMiddleware(id);
};

// 4. Checking Vehicle
export const handleCheckingVehicle = (id) => {
  const url = `${OPEN_API}v1/capture/sessions/${id}/details`;
  return callApis('GET', url, {}, false, false);
};

// 5. Odom Submit
export const handleUpdateOdom = (id, data) => {
  const url = `${OPEN_API}v1/capture/sessions/${id}/odometer`;
  return callApis('PUT', url, data, false, false, false, true);
};

// 6. Native Gallery
export const handlegetNativeGallery = (id) => {
  const withToken = AGENT_TOKEN;
  const url = `${BASE_URL}native_photos/session/${id}`;
  return callApis('GET', url, {}, withToken, false);
};

// 7. Upload photos native
export const handleUploadNativePhotos = (id, img, shot, config) => {
  const url = `${IMG_URL_UPLOAD}`;
  const data = new FormData();
  data.append('image', img, `${id}_${shot}.png`);
  data.append('photo_code', shot);
  data.append('session_key', id);
  return callApis('POST', url, data, false, config);
};

// 8. Delete Native Photos
export const handleDeleteNativePhotos = (id, key) => {
  const url = `${BASE_URL}native_photos/session/${id}`;
  const data = new FormData();
  data.append('photo_code', key);
  return callApis('DELETE', url, data, false, false);
};

// 9. Upload photos
export const handleUploadPhotos = (id, img, shot, config) => {
  const url = `${OPEN_API}v1/capture/photos/${id}`;
  const data = new FormData();
  data.append('image', img);
  data.append('photo_code', shot);
  return callApis('POST', url, data, false, config);
};

export const handleUploadPhotosDispute = (id, img, photo_id, config, photo_code) => {
  const url = `${OPEN_API}v2/disputes/capture/${id}`;
  const data = new FormData();
  data.append('image', img);
  data.append('photo_id', photo_id);
  data.append('photo_code', photo_code);
  return callApis('POST', url, data, false, config);
};

// 9b. View Upload photos
export const handleViewUploadPhotos = (id) => {
  const url = `${OPEN_API}v1/capture/photos/${id}`;
  return callApis('GET', url);
};

// 9c. Request to skip capture a photo
export const handleSkipCapturePhoto = (sessionKey, photoCode) => {
  return callApis(
    'PUT',
    `${OPEN_API}v1/capture/photos/${sessionKey}/skip`,
    { photo_code: photoCode },
    false,
    false,
    false,
    true
  );
};

// 10. Get disclosures data
export const getDisclosuresAPI = (urlcustom) => {
  const date = new Date();
  const timestamp = date.getTime();
  const url = `${urlcustom}?${timestamp}`;
  const withToken = AGENT_TOKEN;
  return callApis('GET', url, {}, withToken, false);
};

// 11. Checking Progress
export const checkingProgressCapAPI = (id) => {
  const withToken = AGENT_TOKEN;
  const url = `${BASE_URL}sessions/${id}`;
  return callApis('GET', url, {}, withToken, false);
};

// 11. Finish Progress
export const getResultSession = (id, query) => {
  // const withToken = AGENT_TOKEN;
  const url = query
    ? `${BASE_DAMAGES_TIRES}inspection-result/${id}${query}`
    : `${BASE_DAMAGES_TIRES}inspection-result/${id}`;
  return callApis('GET', url, {});
};

// 12. Discloseures && announcement
export const sendDisclosuresAnnouncementsData = (data, type) => {
  const formData = new FormData();
  Object.keys(data).forEach((key) => formData.append(key, data[key]));
  const url = `${BASE_URL}${type}`;
  return callApis('POST', url, formData, false, false);
};

// 13. user-update-tire
export const handleUserUpdatedTire = (sessionID, data) => {
  const withToken = AGENT_TOKEN;
  const url = `${AGENT_BASE_URL}tasks/user-update-tire/${sessionID}`;
  return callApis('PUT', url, data, withToken, false, false, true);
};

// 14. Sync damages
export const handleSyncDamages = (sessionID) => {
  const data = new FormData();
  data.append('session_key', sessionID);
  const url = `${BASE_URL}sync_damages`;
  const withToken = AGENT_TOKEN;
  return callApis('POST', url, data, withToken, false);
};

// 15. Submit Survey
export const handleSubmitSurvey = (sessionID, submitKey, selection) => {
  const data = new FormData();
  data.append('session_key', sessionID);
  data.append(submitKey, selection);
  const url = `${BASE_URL}pre_disclosures`;
  return callApis('PUT', url, data, false, false, false, true);
};

// 16. Pavepass get notify
export const handleGetNotify = (id) => {
  const url = `${PAVE_PASS_BASE_URL}${id}`;
  return callApis('GET', url, {}, false, false, true);
};

// 17. Update Damage-Tire
export const handleUpdateDamageTire = (id, data) => {
  const withToken = AGENT_TOKEN;
  const url = `${AGENT_BASE_URL}tasks/user-update-tire/${id}`;
  return callApis('PUT', url, data, withToken, false, false, true);
};

// 18. Updated vehicle
export const handleUpdateVehicle = (vehId, data, updateEndpoint) => {
  const formData = new FormData();
  for (const dataKey in data) {
    const dataValue = data[dataKey];
    if (dataValue) {
      formData.append(dataKey, dataValue);
    }
  }
  const url = `${BASE_URL}${updateEndpoint}/${vehId}`;
  return callApis('POST', url, formData, false, false);
};

// 19. Localizing
export const handleTranslateJson = (query) => {
  const url = `${PAVE_LOCALIZATION}/capture-ui-translations${query}`;
  return callApis('GET', url);
};

// // 19. Localizing
export const handleTranslateAPI = (data) => {
  const withToken = AGENT_TOKEN;
  const url = `${PAVE_LOCALIZATION}/vehicle-translation`;
  return callApis('POST', url, data, withToken, false, false, true);
};
// // // 19. Localizing
// export const handleTranslateAPI = (data) => {
//   const withToken = AGENT_TOKEN;
//   const url = `${AGENT_BASE_URL}inspections/vehicle-translate`;
//   return callApis('POST', url, data, withToken, false, false, true);
// };

// 20. Send sms
export const handleSendSMS = (phone, type, id) => {
  let data = {};
  let url = '';
  const withToken = NOTIFY_TOKEN;
  if (type === '_NOT_MOBILE') {
    url = `${NOTIFY_URL}sms/send`;
    const getUrl = window.location.href;
    const formData = new FormData();
    formData.append('to', phone);
    formData.append(
      'message',
      `Here's your requested PAVE Capture URL - ${getUrl}`
    );
    data = formData;
  } else {
    data = {
      session_key: id,
      phone: phone,
    };
    url = `${NOTIFY_URL}notify`;
  }
  return callApis('POST', url, data, withToken, false, false, true);
};

// 21. Get api from flexible by url
export const getApiUrl = (url) => {
  return callApis('GET', url, {});
};

// 22. Damages v2
export const getUserDamages = (type, query) => {
  const urlQuery = {
    [DAMAGES_VERSION.V1]: `${BASE_DAMAGES_TIRES}pave-damages${query}`,
    [DAMAGES_VERSION.V2]: `${BASE_DAMAGES_TIRES}damages${query}`,
    [DAMAGES_VERSION.BMW]: `${DAMAGES_BMW}${query}`,
  };
  return callApis('GET', urlQuery[type], {});
};

//23 Vin decode
export const handleVinDecode = (id, value) => {
  const data = {
    input: `${value}`.toUpperCase(),
    type: 'vin',
    source: 'capture', // optional
  };

  const url = `${OPEN_API}v2/capture/sessions/${id}/vehicle-number`;
  return callApis('POST', url, data, false, false, false, true);
};

//24 pdated damages
export const updateDamagesAPI = (id, index, status, query) => {
  // const data = new FormData();
  // data.append('session_key', id);
  // data.append('damage_index', index);
  // data.append('response', status);
  const body = {
    session_key: id,
    damage_index: index,
    response: status,
  };
  const url = `${BASE_DAMAGES_TIRES}update-damage${query}`;
  return callApis('PUT', url, body);
};

// 25 End Session Capture
export const handleFinishSession = (id) => {
  const data = new FormData();
  data.append('session_key', id);
  const url = `${BASE_URL}stop`;
  return callApis('POST', url, data);
};

// 26 Confirm session at Announcements
export const handleConfirmSession = (id) => {
  const url = `${BASE_URL}confirm`;
  const data = new FormData();
  data.append('session_key', id);
  return callApis('POST', url, data);
};

// 27 User add images
export const handleAdvanceUserUploadPhotos = (
  id,
  image,
  index,
  isPrimary = false
) => {
  const data = new FormData();
  data.append('image', image);
  if (isPrimary) {
    data.append('primary', 'true');
  }
  data.append('index', index);
  data.append('session_key', id);
  const url = `${IMG_URL_UPLOAD_USER}`;
  return callApis('POST', url, data);
};

// 28 handle translate multi-langs
export const handleUpdateMultiLangs = (data) => {
  const url = `${AGENT_BASE_URL}inspections/translate`;
  return callApis('POST', url, data);
};

// 29. Edit damages Tires
export const handleEditDamageTires = (sessionID, data) => {
  const url = `${BASE_DAMAGES_TIRES}user-tire/${sessionID}`;
  return callApis('POST', url, data);
};

// 30. Get Tire Size
export const handleGetTiresSize = (width, ratio) => {
  let validParams = '';
  if (!!width) {
    validParams = `?width=${width}`;
  }
  if (!!ratio) {
    validParams = `?ratio=${ratio}`;
  }
  if (!!width && !!ratio) {
    validParams = `?width=${width}&ratio=${ratio}`;
  }

  // &width=${width}&ratio=${ratio}
  const url = `${BASE_DAMAGES_TIRES}tire/size${validParams}`;
  return callApis('GET', url);
};

// 31. get tire damages
export const handleGetDamagesTires = (id, component) => {
  const url = `${BASE_DAMAGES_TIRES}tires/${id}?component=${component}`;
  return callApis('GET', url);
};

// 32. checking white label
export const getSessionData = (id) => {
  const url = `${OPEN_API}v1/capture/sessions/${id}`;
  return callApis('GET', url);
};

// 33. get /api/disclosure-announcement
export const handleGetDnA = (locale) => {
  const url = `${PAVE_LOCALIZATION}/disclosure-announcement${locale}`;
  return callApis('GET', url);
};

// 34. rejectedMess-component
export const handleGetRejectedComponent = (query) => {
  const url = query
    ? `${PAVE_LOCALIZATION}/rejected-component${query}`
    : `${PAVE_LOCALIZATION}/rejected-component`;
  return callApis('GET', url);
};

// 35. Skip photoCode
export const skipPhotoCode = (id, data) => {
  const withToken = AGENT_TOKEN;
  const url = `${AGENT_BASE_URL}inspections/${id}/skipPhoto`;
  return callApis('PUT', url, data, withToken, false, false, true);
};

// PAVE Forms API
export const getFormQuestions = (id) => {
  return callApis('GET', `${PAVE_FORMS_API}/v1/clients/${id}`);
};

export const getFormInfo = (sessionKey) => {
  return callApis('GET', `${OPEN_API}v1/forms/${sessionKey}`);
};

export const getFormData = (sessionKey) => {
  return callApis('GET', `${OPEN_API}v1/forms/${sessionKey}/data`);
};

export const submitFormData = (sessionKey, data) => {
  return callApis(
    'PUT',
    `${OPEN_API}v1/forms/${sessionKey}/data`,
    data,
    false,
    false,
    false,
    true
  );
};

export const removeAttachment = (sessionKey, data) => {
  return callApis(
    'DELETE',
    `${OPEN_API}v1/forms/${sessionKey}/attachments`,
    data,
    false,
    false,
    false,
    true
  );
};

//
export const updateVehicleData = (id, payloadData) => {
  // const url = `${REACT_APP_PAVE_VEHICLES_API}/vehicle/create/` + id;
  // return callApis('PUT', url, data);

  const { vin, year, make, model, selectedVINType } = payloadData;

  const data = {
    input: `${vin}`.toUpperCase(),
    type: 'custom',
    input_prefix: selectedVINType,
    source: 'capture', // optional
    vehicle: {
      // required if type=='create'
      //  "vin": "UPDATE_VIN_HERE",
      year: year,
      make: make,
      model: model,
    },
  };

  const url = `${OPEN_API}v2/capture/sessions/${id}/vehicle-number`;
  return callApis('POST', url, data, false, false, false, true);
};

export const handlePaveRedirect = (id) => {
  const url = `${BASE_URL}sessions/${id}`;
  return callApis('GET', url);
};

export const getDisputeDetail = (uuid) => {
  return callApis('GET', `${OPEN_API}v2/disputes/capture/${uuid}`);
};

export const getAPI = (urlcustom) => {
  return axiosInstance({
    method: 'get',
    url: BASE_URL + urlcustom,
  });
};

const apiAction =
  (method = 'post') =>
  (type, url, body = {}, withToken = false, async = true) => ({
    type,
    meta: {
      body,
      method,
      async,
      withToken,
      path: url,
    },
  });

export default apiAction;
